import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Form, Row, Col, Container } from "react-bootstrap";
import FormInput from "../../components/ui/FormInput";
import { nomineeBusinessDetailsFields, handleFileChange } from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "../../components/shared/ErrorMessage";

const NomineeBusinessDetails = ({
  memberId,
  nomineeId,
  nomineeBusinessDetailsData,
  setNomineeBusinessDetailsData,
  handleNext,
  type,
  locked = false,
}) => {
  const navigate = useNavigate();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    defaultValues: nomineeBusinessDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});
  const [fieldsData, setFieldsData] = useState([]);

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await nomineeBusinessDetailsFields();
      setFieldsData(data);
    };
    fetchFieldsData();
  }, []);

  const businessCategoryId = watch("businessCategoryId");

  useEffect(() => {
    const fetchFieldsData = async () => {
      const data = await nomineeBusinessDetailsFields(businessCategoryId);
      setFieldsData(data);
    };
    if (businessCategoryId) {
      fetchFieldsData();
    }
  }, [businessCategoryId]);

  // Employee or Business — only one set of fields applies. Mirrors
  // MemberBusinessDetails.js; the two tabs share the same field list and have
  // already drifted apart once.
  const occupationType = watch("occupationType");

  // Saved before this dropdown existed, so it may hold Employee AND Business
  // data. Read from the LOADED record, never from watch() — derived from the
  // live value it would flip as soon as a type was picked, and the clear below
  // would fire on exactly the records it must not touch.
  const isLegacyRecord = Boolean(
    nomineeBusinessDetailsData?.id && !nomineeBusinessDetailsData?.occupationType
  );

  // Legacy records show everything, so no saved value is ever invisible.
  const visibleFields = fieldsData.filter(
    (f) =>
      !f.occupation ||
      isLegacyRecord ||
      !occupationType ||
      f.occupation === occupationType
  );

  // Clear the set that just became hidden, so a Business record cannot carry a
  // stray Monthly Salary.
  useEffect(() => {
    // Legacy records are exempt: picking a type must not discard the other half
    // of what is already stored.
    if (isLegacyRecord) return;
    if (!occupationType) return;
    fieldsData
      .filter((f) => f.occupation && f.occupation !== occupationType)
      .forEach((f) => setValue(f.name, f.type === "checkbox" ? false : ""));
  }, [occupationType, fieldsData, setValue, isLegacyRecord]);

  useEffect(() => {
    if (nomineeBusinessDetailsData) {
      reset(nomineeBusinessDetailsData); // Reset form with new data
      Object.keys(nomineeBusinessDetailsData).forEach((key) => {
        setValue(key, nomineeBusinessDetailsData[key]);
        if (key.endsWith("Image") && nomineeBusinessDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${nomineeBusinessDetailsData[key]}`,
          }));
        }
      });
    }
  }, [nomineeBusinessDetailsData, setValue, reset]);

  const onSubmit = async (submittedData) => {
    // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
    // URL's `type`. That flag describes the loan; this tab is optional, so a
    // loan edited here may never have had a row at all. Sending type:"Edit" with
    // an absent id makes JSON.stringify drop the key, and the server refuses
    // with "Update requires a valid ID." — 5,772 of 6,414 loans have no row here.
    const existingId = nomineeBusinessDetailsData?.id;
    const isNew = !existingId;

    const { id: _ignoredId, ...fields } = submittedData;

    const payload = {
      ...fields,
      model: "nominee_business_details",
      type: isNew ? "Add" : "Edit",
      // Both required, or the new row is orphaned.
      memberId,
      nomineeId,
    };
    if (!isNew) payload.id = existingId;

    console.log("memberId:", memberId);
    console.log("nomineeId:", nomineeId);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetails`,
        payload
      );

      if (response.status === 201) {
        setNomineeBusinessDetailsData(response.data.savedData);
        console.log(
          "Nominee Business Details updated successfully: ",
          response.data
        );
        setIsEditing(false);

        if (type === "Add") {
          handleNext();
        }
        if (isEditing) {
          navigate("/addEditBusinessLoan", {
            state: { type: "View", stepNo: 5, memberId },
          });
        }
        setErrorMsg("");
      } else {
        setErrorMsg(response.data.error || "Error updating nominee business details");
      }
    } catch (error) {
      console.error("Error updating Nominee Business Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating nominee business details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(nomineeBusinessDetailsData); // Reset to original data
    setFilePreviews({}); // Clear any new file previews
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">
        Nominee Business Details
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            className="ms-3 mt-2 btn-hover"
            onClick={handleEditClick}
          >
            Edit
          </Button>
        )}
      </h2>
      <ErrorMessage message={errorMsg} />
      <Form onSubmit={handleSubmit(onSubmit, onError)}>
        <Row>
          {visibleFields.map((field, index) => (
            <Col
              xs={12}
              md={6}
              key={index}
              className="mb-3"
            >
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                control={control}
                errors={errors}
                options={field.options || []}
                readOnly={(type === "View" && !isEditing) || field.readOnly}
                handleFileChange={(e) =>
                  handleFileChange(e, field.name, setFilePreviews)
                }
                existingFileUrl={existingFiles[field.name] || ""}
                filePreview={filePreviews[field.name]?.previewUrl || ""}
                required={field.required}
                alphaOnly={field.alphaOnly}
              />
            </Col>
          ))}
        </Row>

        <div className="text-center mt-3">
          {(type !== "View" || isEditing) && (
            <Button variant="success" type="submit" className="me-2">
              {isEditing ? "Update" : "Submit"}
            </Button>
          )}
          {isEditing && (
            <Button
              variant="warning"
              onClick={handleCancelEdit}
              className="me-2"
            >
              Cancel
            </Button>
          )}
          {!locked && type === "View" && !isEditing && (
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/addEditBusinessLoan", {
                  state: { type: "View", stepNo: 5, memberId },
                })
              }
            >
              Back
            </Button>
          )}
        </div>
      </Form>
    </Container>
  );
};

export default NomineeBusinessDetails;